import React, { useState } from "react";
import { Info, XCircle } from "@phosphor-icons/react";

/**
 * Petit "i" cliquable qui ouvre un popover flottant pour expliquer à quoi sert
 * un bouton d'action du CRM d'appels. Sert de mini-guide contextuel pour les
 * nouvelles téléprospectrices.
 *
 * Props:
 *   - title : titre affiché en haut du popover
 *   - accentColor : "secondary" (rouge/orange), "primary" (vert), "muted" (gris),
 *                   "warm" (orange chaud). Contrôle bordure + marqueur des listes.
 *   - testidPrefix : préfixe unique pour data-testid (ex: `dnc-info-abc123`)
 *   - children : contenu structuré (JSX). Recommandé : titres <div className="font-bold">
 *     et listes <ul className="list-disc list-inside space-y-0.5 mb-2">.
 */
const ACCENT_STYLES = {
  secondary: {
    icon: "border-secondary/40 text-secondary/70 hover:bg-secondary/10",
    iconActive: "bg-secondary text-white border-secondary",
    panel: "border-secondary",
    title: "text-secondary",
  },
  primary: {
    icon: "border-primary/40 text-primary/70 hover:bg-primary/10",
    iconActive: "bg-primary text-white border-primary",
    panel: "border-primary",
    title: "text-primary",
  },
  muted: {
    icon: "border-muted-foreground/40 text-muted-foreground/70 hover:bg-muted",
    iconActive: "bg-muted-foreground text-white border-muted-foreground",
    panel: "border-muted-foreground",
    title: "text-muted-foreground",
  },
  warm: {
    icon: "border-[#C8795A]/40 text-[#C8795A]/70 hover:bg-[#C8795A]/10",
    iconActive: "bg-[#C8795A] text-white border-[#C8795A]",
    panel: "border-[#C8795A]",
    title: "text-[#C8795A]",
  },
};

export default function ActionInfoPopover({ title, accentColor = "secondary", testidPrefix, children }) {
  const [open, setOpen] = useState(false);
  const st = ACCENT_STYLES[accentColor] || ACCENT_STYLES.secondary;

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className={`w-4 h-4 inline-flex items-center justify-center rounded-full border transition-colors ${open ? st.iconActive : st.icon}`}
        data-testid={`${testidPrefix}-btn`}
        aria-label={title}
        title={title}
      >
        <Info size={10} weight="bold"/>
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/20 sm:bg-transparent"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className={`fixed left-4 right-4 top-1/2 -translate-y-1/2 max-h-[80vh] overflow-y-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:translate-y-0 sm:max-h-none sm:overflow-visible z-40 w-auto sm:w-[360px] bg-card border rounded-sm shadow-2xl p-4 text-xs leading-relaxed ${st.panel}`}
            data-testid={`${testidPrefix}-panel`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className={`font-bold uppercase tracking-wider text-[10px] ${st.title}`}>
                {title}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 shrink-0"
                aria-label="Fermer"
                data-testid={`${testidPrefix}-close`}
              >
                <XCircle size={14} weight="bold"/>
              </button>
            </div>
            <div className={accentColor === "primary" ? "marker:text-primary" : accentColor === "muted" ? "marker:text-muted-foreground" : accentColor === "warm" ? "marker:text-[#C8795A]" : "marker:text-secondary"}>
              {children}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
